import React, { Component } from 'react';
import Pagination from 'react-bootstrap/Pagination';
import styles from 'components/ui/pagination/BlazePagination/BlazePagination.module.css';
import { Form } from 'react-bootstrap';

type BlazePaginationProps = {
  pageSize: number;
  pageSizeSelectedHandler?: (pageSize: number) => void;
  showNavComponents?: boolean;
  showPageSizeDropdown?: boolean;
  pageSizeOptions?: number[];
  total: number;
  activePage: number;
  pageSelectedHandler: (pageNumber: number) => void;
  className?: string;
  paginationClassName?: string;
};

/**
 * Blaze Pagination
 *
 * <pre>
 *   Usage example:
 *   <code>
 *     <BlazePagination pageSize={15} total={transactions.length} activePage={1}
 *   </code>
 * </pre>
 */

class BlazePagination extends Component<BlazePaginationProps> {
  ellipsisThreshold = 4;

  state = {
    pageSize: this.props.pageSize,
  };

  componentDidUpdate() {
    // Update pageSize state from outside if changes
    if (this.props.pageSize !== undefined && this.state.pageSize !== this.props.pageSize) {
      this.setState({ pageSize: this.props.pageSize });
    }
  }

  setCurrentPage = (activePageNo: number, e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e !== undefined) {
      e.preventDefault();
    }
    this.props.pageSelectedHandler(activePageNo);
  };

  getNoOfFullPages = (total: number, pageSize: number) => {
    const noOfItemsOnLastPage = total % pageSize;
    let noOfFullPages = Math.floor(total / pageSize);
    if (noOfItemsOnLastPage !== 0) {
      noOfFullPages++;
    }
    return noOfFullPages;
  };

  prevPageClicked = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    let currentPage = this.props.activePage;
    this.props.pageSelectedHandler(--currentPage);
  };

  nextPageClicked = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    let currentPage = this.props.activePage;
    this.props.pageSelectedHandler(++currentPage);
  };

  callPageSizeHandler() {
    if (this.props.pageSizeSelectedHandler) {
      this.props.pageSizeSelectedHandler(this.state.pageSize);
    }
  }

  updatePageSize(value: any) {
    this.setState({ pageSize: value }, () => {
      this.callPageSizeHandler();
      this.props.pageSelectedHandler(1);
    });
  }

  renderResolver(withPageSizeDropdown: JSX.Element, paginationCounter: string) {
    if (this.props.showPageSizeDropdown) {
      return withPageSizeDropdown;
    } else {
      return paginationCounter;
    }
  }

  render() {
    const { total, activePage } = this.props;

    const noOfFullPages = this.getNoOfFullPages(total, this.state.pageSize);
    const pageSelectionButtons = [];
    const prevButtonClassName = activePage === 1 ? 'page-item disabled' : '';
    const nextButtonClassName = activePage === noOfFullPages ? 'page-item disabled' : '';

    pageSelectionButtons.push(
      <div className={prevButtonClassName} key={'first'}>
        <a
          className="page-link"
          href="#"
          title="First"
          onClick={event => {
            this.setCurrentPage(1, event);
          }}
        >
          First
        </a>
      </div>,
    );
    pageSelectionButtons.push(
      <div className={prevButtonClassName} key={'prev'}>
        <a
          className="page-link"
          href="#"
          title="Prev"
          onClick={event => {
            this.prevPageClicked(event);
          }}
        >
          Prev
        </a>
      </div>,
    );

    //no need to render ellipsis (...)
    if (noOfFullPages < 10) {
      for (let i = 1; i < noOfFullPages + 1; i++) {
        pageSelectionButtons.push(
          <Pagination.Item
            key={i}
            id={`qa-pagination-item-${i}`}
            title={String(i)}
            active={i === activePage}
            onClick={() => {
              this.setCurrentPage(i);
            }}
          >
            {i}
          </Pagination.Item>,
        );
      }
    } else {
      //prev 1,2,3,(4),5 ... 100 next
      if (activePage <= this.ellipsisThreshold) {
        for (let i = 1; i < 5 + 1; i++) {
          pageSelectionButtons.push(
            <Pagination.Item
              key={i}
              id={`qa-pagination-item-${i}`}
              title={String(i)}
              active={i === activePage}
              onClick={() => {
                this.setCurrentPage(i);
              }}
            >
              {i}
            </Pagination.Item>,
          );
        }
        pageSelectionButtons.push(<Pagination.Ellipsis key={'ellipsis_2'} />);
        pageSelectionButtons.push(
          <Pagination.Item
            key={noOfFullPages}
            id={`qa-pagination-item-${noOfFullPages}`}
            title={String(noOfFullPages)}
            active={noOfFullPages === activePage}
            onClick={() => {
              this.setCurrentPage(noOfFullPages);
            }}
          >
            {noOfFullPages}
          </Pagination.Item>,
        );
      }
      //prev 1 ... 4,(5),6 ... 100 next || prev 1 ... 95,(96),97 ... 100 next
      if (activePage > this.ellipsisThreshold && activePage <= noOfFullPages - this.ellipsisThreshold) {
        pageSelectionButtons.push(
          <Pagination.Item
            key={1}
            id={`qa-pagination-item-1`}
            title={String(1)}
            active={1 === activePage}
            onClick={() => {
              this.setCurrentPage(1);
            }}
          >
            {1}
          </Pagination.Item>,
        );
        pageSelectionButtons.push(<Pagination.Ellipsis key={'ellipsis_1'} />);

        pageSelectionButtons.push(
          <Pagination.Item
            key={activePage - 1}
            id={`qa-pagination-item-${activePage - 1}`}
            title={String(activePage - 1)}
            onClick={() => {
              this.setCurrentPage(activePage - 1);
            }}
          >
            {activePage - 1}
          </Pagination.Item>,
        );
        pageSelectionButtons.push(
          <Pagination.Item
            key={activePage}
            id={`qa-pagination-item-${activePage}`}
            title={String(activePage)}
            active={true}
            onClick={() => {
              this.setCurrentPage(activePage);
            }}
          >
            {activePage}
          </Pagination.Item>,
        );
        pageSelectionButtons.push(
          <Pagination.Item
            key={activePage + 1}
            id={`qa-pagination-item-${activePage + 1}`}
            title={String(activePage + 1)}
            onClick={() => {
              this.setCurrentPage(activePage + 1);
            }}
          >
            {activePage + 1}
          </Pagination.Item>,
        );

        pageSelectionButtons.push(<Pagination.Ellipsis key={'ellipsis_2'} />);
        pageSelectionButtons.push(
          <Pagination.Item
            key={noOfFullPages}
            id={`qa-pagination-item-${noOfFullPages}`}
            title={String(noOfFullPages)}
            active={noOfFullPages === activePage}
            onClick={() => {
              this.setCurrentPage(noOfFullPages);
            }}
          >
            {noOfFullPages}
          </Pagination.Item>,
        );
      }
      //prev 1 ... 96,(97),98,99,100 next
      if (activePage > noOfFullPages - this.ellipsisThreshold) {
        pageSelectionButtons.push(
          <Pagination.Item
            key={1}
            id={`qa-pagination-item-1`}
            title="1"
            active={1 === activePage}
            onClick={() => {
              this.setCurrentPage(1);
            }}
          >
            {1}
          </Pagination.Item>,
        );
        pageSelectionButtons.push(<Pagination.Ellipsis key={'ellipsis_1'} />);

        for (let i = noOfFullPages - this.ellipsisThreshold; i < noOfFullPages + 1; i++) {
          pageSelectionButtons.push(
            <Pagination.Item
              key={i}
              id={`qa-pagination-item-${i}`}
              title={String(i)}
              active={i === activePage}
              onClick={() => {
                this.setCurrentPage(i);
              }}
            >
              {i}
            </Pagination.Item>,
          );
        }
      }
    }

    pageSelectionButtons.push(
      <div className={nextButtonClassName} key={'next'}>
        <a
          className="page-link"
          href="#"
          title="Next"
          onClick={event => {
            this.nextPageClicked(event);
          }}
        >
          Next
        </a>
      </div>,
    );
    pageSelectionButtons.push(
      <div className={nextButtonClassName} key={'last'}>
        <a
          className="page-link"
          href="#"
          title="Last"
          onClick={event => {
            this.setCurrentPage(noOfFullPages, event);
          }}
        >
          Last
        </a>
      </div>,
    );

    const fromPages = activePage * this.state.pageSize - this.state.pageSize + 1;
    const toPages = activePage * this.state.pageSize < total ? activePage * this.state.pageSize : total;
    const showPaginationComponents = this.props.showNavComponents !== undefined ? this.props.showNavComponents : true;
    const paginationCounter = fromPages + ' - ' + toPages + ' of ' + total;
    const pageSizeDropdownItems = this.props.pageSizeOptions ? this.props.pageSizeOptions : [5, 10, 25, 50];

    const pageSizeDropdown = (
      <Form.Control
        as="select"
        className="custom-select"
        value={this.state.pageSize.toString()}
        onChange={(e: any) => this.updatePageSize(e.target.value)}
      >
        {pageSizeDropdownItems.map(item => (
          <option key={item} value={item.toString()}>
            {item}
          </option>
        ))}
      </Form.Control>
    );
    const withPageSizeDropdown = (
      <div className="d-flex">
        <div className={styles.item}>{paginationCounter} </div>
        <div className={`${styles.item} ${styles.pageSizePicker}`}>
          {this.props.showPageSizeDropdown && pageSizeDropdown}
        </div>
        <div>{this.props.showPageSizeDropdown && 'Records Per Page'}</div>
      </div>
    );

    const navigationComponents = (
      <React.Fragment>
        <div className={'blaze-pagination position-relative ' + this.props.className}>
          <div className="position-absolute mt-3" id={`qa-pagination-total-footer`} title={paginationCounter}>
            {this.renderResolver(withPageSizeDropdown, paginationCounter)}
          </div>
          <Pagination
            className={this.props.paginationClassName ? this.props.paginationClassName : 'justify-content-center'}
          >
            {pageSelectionButtons.map(pageSelectionButton => {
              return pageSelectionButton;
            })}
          </Pagination>
        </div>
      </React.Fragment>
    );

    return <>{(showPaginationComponents || this.props.showPageSizeDropdown) && navigationComponents}</>;
  }
}

export default BlazePagination;
